import { Box, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Dropdown } from '@verantos-dev/shared-components';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { AgentConceptCards } from './AgentConceptCards';
import { AgentPromptPreview } from './AgentPromptPreview';
import { AgentSections } from './AgentSections';
import { AgentValidationPanel } from './AgentValidationPanel';

// TODO: cleanup with cp_annotation_createproject_agent ff

interface PromptBuilderProps {
  modelId: string | null;
  modelOptions: { value: string; displayValue: string }[];
  onModelChange: (event: SelectChangeEvent<unknown>) => void;
}

export const PromptBuilder = ({ modelId, modelOptions, onModelChange }: PromptBuilderProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  return (
    <Box
      id="prompt-builder"
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${borderRadius.md}px`,
        p: theme.spacing(7),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
      }}
    >
      <Box>
        <Typography variant="subtitle1">
          {formatMessage({ id: 'createProjectWizard.step.annotationAgent.builder.title' })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, mt: theme.spacing(1) }}
        >
          {formatMessage({ id: 'createProjectWizard.step.annotationAgent.builder.description' })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: theme.spacing(7),
          alignItems: 'start',
        }}
      >
        <Box
          id="prompt-builder-configuration"
          sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(7) }}
        >
          <Dropdown
            id="annotation-agent-model-dropdown"
            options={modelOptions}
            onChange={onModelChange}
            label={formatMessage({
              id: 'createProjectWizard.step.annotationAgent.builder.model.label',
            })}
            placeholder={formatMessage({
              id: 'createProjectWizard.step.annotationAgent.builder.model.placeholder',
            })}
            required
            value={modelId ?? ''}
            sx={{ width: '100%', '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
          />
          <AgentConceptCards />
          <AgentSections />
          <AgentValidationPanel />
        </Box>
        {/* Sticky inside the wizard's scroll container, so the preview stays in view. It has no
            z-index: the wizard footer is sticky above it, so the preview never covers Next. */}
        <Box id="prompt-builder-preview" sx={{ position: 'sticky', top: theme.spacing(4) }}>
          <AgentPromptPreview />
        </Box>
      </Box>
    </Box>
  );
};
